import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { getAccessTokenNoDispatch } from 'redux/reducers/auth'
import { getCookie } from 'utils/cokie'
import { v4 as uuidv4 } from 'uuid'
import { normalizeDocUtilDownloadUrl } from './docUtilUrl'

const fromContentServer = false
export const isCreditApiEnabled = process.env.REACT_APP_CREDIT_API_ENABLED === 'true'

export const UPLOADURL_DOC_UTIL = process.env.REACT_APP_UPLOADURL_DOC_UTIL

const UPLOAD_MERGE_URL = process.env.REACT_APP_MERGE_URL

const UPLOAD_URL = 'http://192.168.101.174:8080/rest/document/internal/upload'

export const DOWLOAD_URL = ''

const getOnboardingBaseUrl = () => {
  try {
    const kycUrl = process.env.REACT_APP_KYC_EXTRACT_URL || ''
    if (kycUrl) return new URL(kycUrl).origin
  } catch {
    // ignore parse error
  }
}

/**
 * Converts a doc_util_id or resource ID to a full download URL.
 * Normalizes filenames in full doc-utility URLs before returning them.
 * Otherwise, constructs the full download URL using the doc-utility base.
 * If isCreditApiEnabled is true, uses the credit API URL (REACT_APP_UPLOADURL_DOC_UTIL origin) instead.
 */
export function getDocUtilDownloadUrl(resourceIdOrUrl: string): string {
  if (!resourceIdOrUrl) return ''
  if (resourceIdOrUrl.startsWith('https://') || resourceIdOrUrl.startsWith('http://')) {
    return normalizeDocUtilDownloadUrl(resourceIdOrUrl)
  }
  const baseUrl = isCreditApiEnabled
    ? `${new URL(UPLOADURL_DOC_UTIL).origin}/doc-utility/get-output-file-direct`
    : `${getOnboardingBaseUrl()}/bulkupload/v1/doc-utility/get-output-file-direct`
  return `${baseUrl}?doc_util_id=${resourceIdOrUrl}`
}

export const api = axios.create({
  baseURL: '',
  timeout: 30000,
})

api.interceptors.request.use(async (request: InternalAxiosRequestConfig<any>) => {
  const token = getCookie('access_token')
  if (token) {
    request.headers['x-access-token'] = `Bearer ${token}`
  }
  return request
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (error.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      getAccessTokenNoDispatch().then(({ accessToken }) => {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken
      })
      return api(originalRequest)
    }
    return error
  },
)

// TODO: Move to /models
export type DefaultApiResponse<DATATYPE = unknown> = {
  status: boolean
  data: DATATYPE
}

export async function downloadImagefromDocUtil(url: string) {
  const token = getCookie('access_token')
  const headers = isCreditApiEnabled
    ? {
      client_id: process.env.REACT_APP_DOC_UTIL_CLIENT_ID,
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    : {
      client_id: process.env.REACT_APP_DOC_UTIL_CLIENT_ID,
      'Content-Type': 'application/json',
      ...(token ? { 'x-access-token': `Bearer ${token}` } : {}),
    }
  const response: AxiosResponse = await api.get(normalizeDocUtilDownloadUrl(url), {
    headers,
    responseType: 'arraybuffer',
  })
  if (
    response?.data?.status === true ||
    response?.data?.status === 200 ||
    response?.data?.status_code === true ||
    response?.data?.status_code === 200 ||
    response?.status === 200
  ) {
    let contentType = response.headers['content-type']?.toLowerCase() || ''

    let image = ''
    try {
      const bytes = new Uint8Array(response.data)
      // Use chunked conversion to avoid call stack overflow for large files (e.g. PDFs)
      const chunkSize = 8192
      let binary = ''
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.slice(i, i + chunkSize) as any)
      }
      image = btoa(binary)

      // If content-type is octet-stream, detect actual type via magic bytes
      if (contentType.includes('octet-stream') || !contentType) {
        const hex = Array.from(bytes.slice(0, 16))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
        if (hex.startsWith('25504446')) {
          contentType = 'application/pdf' // %PDF
        } else if (hex.startsWith('89504e47')) {
          contentType = 'image/png' // ‰PNG
        } else if (hex.startsWith('ffd8ff')) {
          contentType = 'image/jpeg' // ÿØÿ
        } else if (hex.startsWith('47494638')) {
          contentType = 'image/gif' // GIF8
        }
      }
    } catch {
      console.error('error converting file to base64')
    }

    return {
      data: `data:${contentType};base64,${image}`,
      contentType,
    }
  } else {
    return {
      error: {
        type: 'Download Error',
        code: 'Upload_failed',
        message: 'error in file download refresh the page!',
      },
    }
  }
}

export async function uploadImageOnContentServer(data: FormData) {
  // Check file size before uploading — reject files larger than 10 MB
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
  for (const [key, value] of Array.from(data.entries())) {
    if (value instanceof File && value.size > MAX_FILE_SIZE) {
      return {
        error: {
          type: 'Upload Error',
          code: 'FILE_SIZE_EXCEEDED',
          message: 'File too large. We support a maximum file size of 10 MB.',
        },
      }
    }
  }

  if (fromContentServer) {
    try {
      const response: AxiosResponse = await axios.post(UPLOAD_URL, data, {
        headers: {
          'x-doc-encrypt': true,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      })
      if (
        response?.data?.status === true ||
        response?.data?.status === 200 ||
        response?.data?.status_code === true ||
        response?.data?.status_code === 200 ||
        response?.status === 200
      ) {
        return {
          data: response.data,
        }
      } else {
        return {
          error: {
            type: 'Upload Error',
            code: 'Upload_failed',
            message: 'Please retry with a clear image, avoid screenshots or blurry images.',
          },
        }
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        return {
          error: {
            type: 'Upload Error',
            code: 'FILE_SIZE_EXCEEDED',
            message: 'File too large. We support a maximum file size of 10 MB.',
          },
        }
      }
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        return {
          error: {
            type: 'Upload Error',
            code: 'Upload_failed',
            message: 'Upload timed out. The file may be too large. We support a maximum file size of 10 MB.',
          },
        }
      }
      return {
        error: {
          type: 'Upload Error',
          code: 'Upload_failed',
          message: 'Please retry with a clear image, avoid screenshots or blurry images.',
        },
      }
    }
  } else {
    let file
    for (const value of Array.from(data.entries())) {
      if (value[0] === 'document') {
        file = value[1]
      }
    }

    const formData = new FormData()

    if (isCreditApiEnabled) {
      formData.append('request_id', uuidv4())
      formData.append('product', process.env.REACT_APP_PRODUCT_NAME)
      formData.append('business_use_case', process.env.REACT_APP_BUSINESS_USE_CASE)
      formData.append(
        'doc_name',
        uuidv4() +
        '.' +
        file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length).toLowerCase(),
      )
      formData.append(
        'doc_type',
        file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length).toLowerCase(),
      )
      formData.append('client_doc_unique_identifier_key', uuidv4())
    }
    formData.append('file', file)

    const uploadUrl = isCreditApiEnabled
      ? UPLOADURL_DOC_UTIL
      : `${getOnboardingBaseUrl()}/bulkupload/v1/doc-utility/upload-file-direct`
    const token = getCookie('access_token')

    const response: AxiosResponse = await api.post(uploadUrl, formData, {
      headers: {
        client_id: process.env.REACT_APP_DOC_UTIL_CLIENT_ID,
        ...(token ? { 'x-access-token': `Bearer ${token}` } : {}),
      },
      timeout: 300000,
    })
    if (response?.data?.success === false) {
      return {
        error: {
          type: 'Upload Error',
          code: 'Upload_failed',
          message: response?.data?.message || 'File upload failed, please try again.',
        },
      }
    }
    if (
      response?.data?.status === true ||
      response?.data?.status === 200 ||
      response?.data?.status_code === true ||
      response?.data?.status_code === 200 ||
      response?.status === 200
    ) {
      const resourceId = response?.data?.data?.output_file_download_url
      if (!resourceId) {
        return {
          error: {
            type: 'Upload Error',
            code: 'Upload_failed',
            message: response?.data?.message || 'File upload failed, please try again.',
          },
        }
      }
      return {
        data: {
          st: response.data.success,
          messageCode: '200',
          message: response.data.message,
          resourceId: normalizeDocUtilDownloadUrl(resourceId, file.name),
          docUtilid: response.data?.data?.doc_util_id,
        },
      }
    } else {
      const errorStatus = response?.status || (response as any)?.response?.status
      if (errorStatus === 413) {
        return {
          error: {
            type: 'Upload Error',
            code: 'FILE_SIZE_EXCEEDED',
            message: 'Please ensure that file size is less than 5MB',
          },
        }
      }
      return {
        error: {
          type: 'Upload Error',
          code: 'Upload_failed',
          message: 'Please retry with a clear image, avoid screenshots or blurry images.',
        },
      }
    }
  }
}

export enum ExtractionDocumentType {
  IDENTITY_DOCUMENT = 'IDENTITY_DOCUMENT',
  SSM_DOCUMENT = 'SSM_DOCUMENT',
  BANK_STATEMENT = 'BANK_STATEMENT',
  BANK_AUTHORIZATION_LETTER = 'BANK_AUTHORIZATION_LETTER',
  SIGNATORY_AUTHORIZATION_LETTER = 'SIGNATORY_AUTHORIZATION_LETTER',
  AUTHORIZATION_LETTER = 'AUTHORIZATION_LETTER',
  STORE_DISPLAY_PHOTO = 'STORE_DISPLAY_PHOTO',
  STOCK_PHOTO = 'STOCK_PHOTO',
}

export const EXTRACTION_TYPE_MISMATCH_MESSAGE =
  'It seems that you have uploaded an incorrect document type, so we are unable to extract information or validate the file. Please retry with the correct document, or continue anyway.'

export const getExtractionResult = (payload: unknown): Record<string, unknown> | null => {
  const result = Array.isArray(payload) ? payload[0] : payload
  return result && typeof result === 'object' ? result as Record<string, unknown> : null
}

export const isExtractionTypeMismatch = (payload: unknown): boolean =>
  String(getExtractionResult(payload)?.status || '').trim().toUpperCase() === 'TYPE_MISMATCH'

export const getExtractionWarningMessage = (payload: unknown): string => {
  const result = getExtractionResult(payload)
  const warnings = result?.warnings
  if (Array.isArray(warnings)) {
    const warning = warnings.find((item) => typeof item === 'string' && item.trim())
    if (typeof warning === 'string') return warning.trim()
  }

  const warningMessage = result?.warning_message || result?.warningMessage
  return typeof warningMessage === 'string' ? warningMessage.trim() : ''
}

interface ExtractKycFromFileOptions {
  documentType?: ExtractionDocumentType | string
  requiredDurationMonths?: number
  mcc?: string
  msic?: string
  mccDescription?: string
  msicDescription?: string
}

export async function extractKycFromFile(file: File, options: ExtractKycFromFileOptions = {}) {
  const formData = new FormData()
  formData.append('file', file)
  if (options.documentType) {
    formData.append('documentType', options.documentType)
  }
  if (options.requiredDurationMonths !== undefined) {
    formData.append('requiredDurationMonths', String(options.requiredDurationMonths))
  }
  if (options.mcc) formData.append('mcc', options.mcc)
  if (options.msic) formData.append('msic', options.msic)
  if (options.mccDescription) formData.append('mccDescription', options.mccDescription)
  if (options.msicDescription) formData.append('msicDescription', options.msicDescription)

  // ocr const extractUrl = process.env.REACT_APP_KYC_EXTRACT_URL
  const extractUrl =
    process.env.REACT_APP_KYC_EXTRACT_URL

  const response: AxiosResponse = await api.post(
    extractUrl,
    formData,
    {
      headers: {
        'x-idempotency-id': uuidv4(),
      },
      timeout: options.documentType === 'BANK_STATEMENT' ? 300000 : 100000,
    },
  )

  if (response?.status === 200) {
    if (isExtractionTypeMismatch(response.data)) {
      return {
        data: response.data,
        error: {
          type: 'Incorrect document type',
          code: 'TYPE_MISMATCH',
          message: EXTRACTION_TYPE_MISMATCH_MESSAGE,
        },
      }
    }
    return { data: response.data }
  }

  return {
    error: {
      type: 'Extract Error',
      code: 'EXTRACT_FAILED',
      message: options.documentType === 'BANK_STATEMENT'
        ? 'Error in bank statement extraction. Please try again.'
        : 'Error in document extraction. Please try again.',
    },
  }
}

// Extract UEN details from uploaded document image (base64) via e-extraction API
export async function extractUenFromDocument(base64Data: string) {
  if (!base64Data) {
    return {
      error: {
        type: 'Extract Error',
        code: 'UEN_EXTRACT_FAILED',
        message: 'No document data provided for UEN extraction.',
      },
    }
  }

  const token = getCookie('access_token')

  let baseUrl = ''
  try {
    baseUrl = new URL(process.env.REACT_APP_KYC_EXTRACT_URL || '').origin
  } catch {
    // fallback
  }

  const url = `${baseUrl}/documentExtractor/v2/kyc/document/number/e-extraction`

  try {
    const response: AxiosResponse = await axios.post(
      url,
      {
        documentType: 'UEN_QR',
        documentNumber: base64Data,
      },
      {
        headers: {
          'content-type': 'application/json',
          'x-access-token': token ? `Bearer ${token}` : '',
          'x-idempotency-id': uuidv4(),
        },
        timeout: 100000,
      },
    )

    if (response?.status === 200) {
      return { data: response.data }
    }

    return {
      error: {
        type: 'Extract Error',
        code: 'UEN_EXTRACT_FAILED',
        message: 'Error in UEN document extraction. Please try again.',
      },
    }
  } catch (err: any) {
    return {
      error: {
        type: 'Extract Error',
        code: 'UEN_EXTRACT_FAILED',
        message: err?.response?.data?.message || 'Error in UEN document extraction. Please try again.',
      },
    }
  }
}

// Upload bank statement file to S3 via bulkupload API (Singapore flow only)
export async function uploadBankStatementToS3(file: File, opportunityId: string | number) {
  if (!opportunityId && opportunityId !== 0) {
    return {
      error: {
        type: 'Upload Error',
        code: 'BANK_STATEMENT_UPLOAD_FAILED',
        message: 'Missing opportunity id for bank statement upload.',
      },
    }
  }

  const formData = new FormData()
  formData.append('file', file)

  const url = `${getOnboardingBaseUrl()}/bulkupload/v1/opportunities/${opportunityId}/uploadS3`

  const token = getCookie('access_token')

  try {
    const response: AxiosResponse = await api.post(url, formData, {
      headers: {
        'x-idempotency-id': uuidv4(),
        ...(token ? { 'x-access-token': `Bearer ${token}` } : {}),
      },
      timeout: 300000,
    })

    if (response?.status === 200) {
      return { data: response.data }
    }

    return {
      error: {
        type: 'Upload Error',
        code: 'BANK_STATEMENT_UPLOAD_FAILED',
        message: 'Error in bank statement upload. Please try again.',
      },
    }
  } catch (e: any) {
    if (e?.response?.status === 403) {
      return {
        error: {
          type: 'Upload Error',
          code: 'BLURRY_IMAGE',
          message: 'Please retry with a clear image, avoid screenshots or blurry images.',
        },
      }
    }
    throw e
  }
}

// Upload selfie file to S3 via bulkupload API (Singapore flow)
export async function uploadSelfieToS3(file: File | Blob, opportunityId: string | number) {
  if (!opportunityId && opportunityId !== 0) {
    return {
      error: {
        type: 'Upload Error',
        code: 'SELFIE_S3_UPLOAD_FAILED',
        message: 'Missing opportunity id for selfie S3 upload.',
      },
    }
  }

  const formData = new FormData()
  // Ensure file has a proper filename with extension — S3 API rejects files without one
  const fileName = file instanceof File && file.name ? file.name : 'selfie.jpg'
  formData.append('file', file, fileName)

  const url = `${getOnboardingBaseUrl()}/bulkupload/v1/opportunities/${opportunityId}/uploadS3`

  const token = getCookie('access_token')

  try {
    const response: AxiosResponse = await api.post(url, formData, {
      headers: {
        'x-idempotency-id': uuidv4(),
        ...(token ? { 'x-access-token': `Bearer ${token}` } : {}),
      },
      timeout: 10000,
    })

    if (response?.status === 200) {
      return { data: response.data }
    }

    return {
      error: {
        type: 'Upload Error',
        code: 'SELFIE_S3_UPLOAD_FAILED',
        message: 'Please retry with a clear image, avoid screenshots or blurry images.',
      },
    }
  } catch (e: any) {
    if (e?.response?.status === 403) {
      return {
        error: {
          type: 'Upload Error',
          code: 'BLURRY_IMAGE',
          status: 403,
          message: 'Please retry with a clear image, avoid screenshots or blurry images.',
        },
      }
    }
    throw e
  }
}

// Fetch bank statement details from document-extractor GraphQL using pre-signed URL
export async function fetchBankStatementDetails(preSignedUrl: string) {
  if (!preSignedUrl) {
    return {
      error: {
        type: 'Bank Statement Error',
        code: 'BANK_STATEMENT_DETAILS_FAILED',
        message: 'Missing pre-signed URL for bank statement details.',
      },
    }
  }

  const token = getCookie('access_token')

  const graphqlBody = {
    query:
      'query DocumentDetails($input: DocumentDetailsInput!) {\n  documentDetails(input: $input) {\n    accountNumber\n    address\n    aiGenerated\n    aiGeneratedReasoning\n    bankAddress\n    bankBranch\n    bankCode\n    bankName\n    bicCode\n    confidenceScore\n    currency\n    detectedCountryCode\n    detectedDocumentType\n    detectedLanguage\n    dob\n    email\n    iban\n    ifsc\n    matchResult {\n      countryMatch\n      typeMatch\n    }\n    micrCode\n    phone\n    qualityChecks {\n      documentFullyVisible\n      fingerPresence\n      glareFree\n      readability\n    }\n    sortCode\n    statementPeriodEndDate\n    statementPeriodStartDate\n    swiftCode\n    tamperedDocument\n    tamperedDocumentReasoning\n    type\n  }\n}',
    variables: {
      input: {
        documentType: 'BANK_STATEMENT',
        documentNumber: preSignedUrl,
      },
    },
  }

  const response: AxiosResponse = await api.post(
    process.env.REACT_APP_GRAPHQL_URL || 'https://springboard.saasuat.pinelabs.com/graphql',
    graphqlBody,
    {
      headers: {
        'content-type': 'application/json',
      },
      timeout: 300000,
    },
  )

  if (response?.status === 200 && !response.data?.errors) {
    return { data: response.data?.data?.documentDetails }
  }

  return {
    error: {
      type: 'Bank Statement Error',
      code: 'BANK_STATEMENT_DETAILS_FAILED',
      message: 'Error fetching bank statement details. Please try again.',
    },
  }
}

// Validate phone number using PHONE_NUMBER documentDetails API
export async function validatePhoneNumberWithDocumentDetails(phoneNumber: string) {
  if (!phoneNumber) {
    return {
      error: {
        type: 'Phone Validation Error',
        code: 'PHONE_VALIDATION_FAILED',
        message: 'Missing phone number for validation.',
      },
    }
  }

  const token = getCookie('access_token')

  const graphqlBody = {
    query:
      'query DocumentDetails($input: DocumentDetailsInput!) {\n  documentDetails(input: $input) {\n    blocklisting\n    carrierName\n    city\n    cleansedCallPhoneNumber\n    cleansedSmsPhoneNumber\n    country\n    county\n    metroCode\n    originalCompletePhoneNumber\n    phoneTypeDescription\n    state\n    subscriberStatusCode\n    subscriberStatusDescription\n    zip\n  }\n}',
    variables: {
      input: {
        documentType: 'PHONE_NUMBER',
        documentNumber: phoneNumber,
      },
    },
  }

  const response: AxiosResponse = await api.post(
    process.env.REACT_APP_GRAPHQL_URL || '',
    graphqlBody,
    {
      headers: {
        'content-type': 'application/json',
      },
      timeout: 300000,
    },
  )

  if (response?.status === 200 && !response.data?.errors) {
    return { data: response.data?.data?.documentDetails }
  }

  return {
    error: {
      type: 'Phone Validation Error',
      code: 'PHONE_VALIDATION_FAILED',
      message: 'Error validating phone number. Please try again.',
    },
  }
}

export async function mergeFiles(data: FormData) {
  let file = []
  for (const value of Array.from(data.entries())) {
    if (value[0].includes('document')) {
      file = [...file, value[1]]
    }
  }
  if (file.length <= 0) {
    return {
      error: {
        type: 'Upload Error',
        code: 'Upload_failed',
        message: 'No file selected!',
      },
    }
  }
  const formData = new FormData()

  // formData.append('request_id', uuidv4())
  // formData.append('product', process.env.REACT_APP_MERGE_PRODUCT)
  // formData.append('business_use_case', process.env.REACT_APP_MERGE_BUSINESS_CASE)
  // formData.append(
  //   'doc_name',
  //   uuidv4() +
  //   '.' +
  //   file[0].name.substring(file[0].name.lastIndexOf('.') + 1, file[0].name.length).toLowerCase(),
  // )
  // formData.append(
  //   'doc_type',
  //   file[0].name.substring(file[0].name.lastIndexOf('.') + 1, file[0].name.length).toLowerCase(),
  // )
  // formData.append('client_doc_unique_identifier_key', uuidv4())
  let images = []
  file.map((item, index) => {
    images = [...images, { doc_name: item.name, priority: 1, key: 'file_' + index }]
    formData.append('file_' + index, item)
  })

  const operationData = {
    merged_file_name: 'front_and_back.jpg',
    images: images,
  }
  formData.append('operation_data', JSON.stringify(operationData))

  const response: AxiosResponse = await api.post(UPLOAD_MERGE_URL, formData, {
    headers: {
      client_id: process.env.REACT_APP_DOC_UTIL_CLIENT_ID,
      // origin: process.env.REACT_APP_DOC_UTIL_ORIGIN, // TODO: Remove. Browser will refuse to set unsafe header "origin"
    },
    timeout: 300000,
  })
  if (
    response?.data?.status === true ||
    response?.data?.status === 200 ||
    response?.data?.status_code === true ||
    response?.data?.status_code === 200 ||
    response?.status === 200
  ) {
    return {
      data: {
        st: response.data.success,
        messageCode: '200',
        message: response.data.message,
        resourceId: response.data.data.output_file_download_url,
        docUtilid: response.data.data.doc_util_id,
      },
    }
  } else {
    // api is not success
    return {
      error: {
        type: 'Upload Error',
        code: 'Upload_failed',
        message: 'Please retry with a clear image, avoid screenshots or blurry images.',
      },
    }
  }
}
export async function getDocumentLink(url: string, docUtilId: string) {
  const formData = new FormData()
  formData.append('request_id', uuidv4())
  formData.append('doc_util_id', docUtilId)

  const response: AxiosResponse = await api.post(url, formData, {
    headers: {
      client_id: process.env.REACT_APP_DOC_UTIL_CLIENT_ID,
      // origin: process.env.REACT_APP_DOC_UTIL_ORIGIN, // TODO: Remove. Browser will refuse to set unsafe header "origin"
      'Content-Type': 'application/json',
    },
  })
  if (
    response?.data?.status === true ||
    response?.data?.status === 200 ||
    response?.data?.status_code === true ||
    response?.data?.status_code === 200 ||
    response?.status === 200
  ) {
    return response?.data
  } else {
    return {
      error: {
        type: 'Download Error',
        code: 'Upload_failed',
        message: 'error in file download refresh the page!',
      },
    }
  }
}

/**
 * MYS: Fetch MSIC and MCC codes from nature of business description
 * @param natureOfBusiness - The business description text
 * @returns Promise containing msic and mcc arrays
 */
export async function getMsicMccFromNatureOfBusiness(natureOfBusiness: string): Promise<{
  data?: { msic: Array<{ code: string; description: string }>; mcc: Array<{ code: string; description: string }> };
  error?: { type: string; code: string; message: string };
}> {
  try {
    const baseUrl = getOnboardingBaseUrl()
    const url = `${baseUrl}/documentExtractor/v1/ssic/msic/mcc`
    const token = getCookie('access_token')

    const response: AxiosResponse = await api.post(
      url,
      { natureOfBusiness },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': `Bearer ${token}`,
        },
      }
    )

    if (response?.status === 200 && response?.data) {
      return { data: response.data }
    } else {
      return {
        error: {
          type: 'MSIC_FETCH_ERROR',
          code: 'MSIC_001',
          message: 'Failed to fetch MSIC codes',
        },
      }
    }
  } catch (e) {
    console.error('getMsicMccFromNatureOfBusiness error:', e)
    return {
      error: {
        type: 'MSIC_FETCH_ERROR',
        code: 'MSIC_002',
        message: e?.message || 'Error fetching MSIC codes',
      },
    }
  }
}
